'use client';

import { Configure, InstantSearch } from 'react-instantsearch';
import { liteClient as algoliasearch } from 'algoliasearch/lite';

import NoSsr from '@mui/material/NoSsr';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

import { usePathname, useSearchParams } from 'src/routes/hooks';

import { CONFIG } from 'src/global-config';
import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

import { ProductSelect } from './product-select';

// ----------------------------------------------------------------------

const filters = ['publish:"YES"'];

const { appId, apiKey } = CONFIG.algolia;

const searchClient = algoliasearch(appId, apiKey);

const getSellerID = (pathnameParts) => (pathnameParts[1] === 'profile' ? pathnameParts[2] : '');

export function ProductSearch({ seller, sx }) {
  const pathname = usePathname();

  const searchParams = useSearchParams();

  const { t, currentLang } = useTranslate();

  const query = searchParams.get('query') ?? '';
  const country = searchParams.get('country') ?? currentLang.countryName;
  const sellerID = seller?.id ?? searchParams.get('sellerID') ?? getSellerID(pathname.split('/'));

  if (country) filters.push(`country:"${country}"`);
  if (sellerID) filters.push(`sellerID:"${sellerID}"`);

  const renderFallback = () => (
    <TextField
      size="small"
      defaultValue={query}
      placeholder={`${t('Search products, brands and more')}...`}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
          sx: {
            borderRadius: { xs: 1, md: 2.5 },
            height: { xs: 45, md: 'initial' },
            backgroundColor: 'background.paper',
          },
        },
      }}
    />
  );

  return (
    <Stack sx={[{ width: 1 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <NoSsr fallback={renderFallback()}>
        <InstantSearch
          searchClient={searchClient}
          indexName="search_promotions"
          future={{ preserveSharedStateOnUnmount: true }}
          initialUiState={{ search_promotions: { query } }}
        >
          <Configure filters={filters.join(' AND ')} />
          <ProductSelect initialQuery={query} />
        </InstantSearch>
      </NoSsr>
    </Stack>
  );
}
